import { useCallback, useRef, useState } from "react"

import { generatePersona } from "~/api/message-genie"
import { getSinglePersonBriefStream } from "~/api/pre-meeting"
import useGlobalStore from "~/store/global"
import type { ITokenInfo } from "~/types/global"
import { track } from "~/utils/amplitude"
import {
  EVENTS as AMPLITUDE_EVENTS,
  getBaseEventProps
} from "~/utils/amplitude-events"
import { applyPersonBriefFrame } from "~/utils/person-brief-frames"
import { sleep } from "~/utils/sleep"

import { EVENTS } from "../knowledge-os/pre-meeting/constants"

// Persona identification poll ceiling: 20 attempts at 3s is one minute. Past
// that the job is not coming back on this page view — 287 rows on dev have been
// stuck in progress for up to a year — so the section reports unavailable
// rather than spinning for as long as the tab stays open.
const PERSONA_POLL_INTERVAL_MS = 3000
const PERSONA_POLL_MAX_ATTEMPTS = 20

// Generation half of the Pre-Meeting Brief (v1), lifted verbatim out of
// PersonBriefDrawer so it can drive a second surface.
//
// Person 360 now renders the brief INLINE rather than behind a button, so the
// same two-task flow needs to run outside a Drawer. Extracting it beats copying
// it: the SSE frame handling, the persona polling and the several deliberate
// error asymmetries below are easy to get subtly wrong twice and then drift.
//
// The two tasks run in parallel and are NOT equals:
//   • the brief stream is the payload — its failure sets isApiFailed
//   • persona ("Know your prospect") is secondary — its failure must NOT, or
//     the render gates the whole brief on it and replaces a successfully
//     streamed brief with an error alert
export interface IUsePersonBriefArgs {
  linkedin_url: string
  personName: string
  account_name: string
  // Apollo ORGANISATION id. Goes on the wire as organization_id.
  accountId?: number
  // Our internal DB account id. Goes on the wire as account_id, a SEPARATE
  // field the backend uses to resolve the apollo org id itself. The two are not
  // interchangeable: passing a DB id as accountId sends it as an apollo org id
  // and the lookup silently finds nothing. People OS only ever has this one.
  accountDbId?: number | string | null
  // Amplitude label for where the brief was launched from. The drawer derives
  // it from the URL hash; other surfaces pass their own.
  source: string
  // Called instead of starting when there is no LinkedIn URL. The brief is
  // keyed on the profile, so it cannot run without one.
  onMissingLinkedin?: () => void
}

export function usePersonBrief({
  linkedin_url,
  personName,
  account_name,
  accountId,
  accountDbId,
  source,
  onMissingLinkedin
}: IUsePersonBriefArgs) {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)

  const [personBrief, setPersonBrief] = useState<any>(null)
  const [persona, setPersona] = useState<any>(null)
  const [isApiFailed, setIsApiFailed] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [isPersonBriefLoading, setPersonBriefLoading] = useState(false)
  const [personaLoading, setPersonaLoading] = useState(false)

  const pollCancelledRef = useRef(false)
  const streamAbortRef = useRef<AbortController | null>(null)

  // Bounded. This polled IDENTIFICATION_IN_PROGRESS forever: sleep 3s, recurse,
  // no attempt cap and no deadline. On dev custom_personas holds 287 rows stuck
  // in that state, the oldest since April 2025, so "Know your prospect" span a
  // spinner indefinitely for any of them. It mattered more once Person 360
  // started generating the brief automatically on open instead of on a click.
  //
  // Any state we do not recognise is now TERMINAL, not loading. 43 rows carry
  // "IDENTIFIED_IN_PROGRESS" — a different string from the one handled here —
  // which previously fell through every branch leaving personaLoading true
  // forever, so the section span without even polling.
  const pollPersona = useCallback(
    async (attempt = 1): Promise<any> => {
      if (pollCancelledRef.current) return null

      setPersonaLoading(true)
      const response = await generatePersona(tokenInfo!, { linkedin_url })
      const state = response.data.data.persona_idetification_state
      if (state === "IDENTIFIED" || state === "INVALID_LINKEDIN_URL") {
        setPersona(response.data.data)
        setPersonaLoading(false)
        track(AMPLITUDE_EVENTS.PERSONA.FETCH_PERSONA, {
          ...getBaseEventProps({
            feature: "pre_meeting_brief",
            accountId,
            accountName: account_name
          }),
          name: response.data.data.name,
          id: response.data.data.id,
          state: state === "IDENTIFIED" ? "success" : "failed"
        })
        return null
      }
      if (state === "IDENTIFICATION_IN_PROGRESS") {
        if (attempt >= PERSONA_POLL_MAX_ATTEMPTS) {
          // Give up rather than spin. persona stays null, so the section
          // renders as unavailable instead of pretending to still be working.
          setPersonaLoading(false)
          track(AMPLITUDE_EVENTS.PERSONA.FETCH_PERSONA, {
            ...getBaseEventProps({
              feature: "pre_meeting_brief",
              accountId,
              accountName: account_name
            }),
            name: response.data.data.name,
            id: response.data.data.id,
            state: "timeout"
          })
          return null
        }
        const { promise, clear } = sleep(PERSONA_POLL_INTERVAL_MS)
        await promise
        clear()
        return pollPersona(attempt + 1)
      }
      // Unrecognised state: stop, do not leave the spinner running.
      setPersonaLoading(false)
      return null
    },
    [linkedin_url, tokenInfo, accountId, account_name]
  )

  // Aborts the in-flight stream and stops persona polling. Safe to call when
  // nothing is running.
  const cancel = useCallback(() => {
    pollCancelledRef.current = true
    streamAbortRef.current?.abort()
    setIsStreaming(false)
  }, [])

  const reset = useCallback(() => {
    cancel()
    setPersona(null)
    setPersonBrief(null)
  }, [cancel])

  const start = useCallback(async () => {
    if (!linkedin_url) {
      onMissingLinkedin?.()
      return
    }
    setPersonBriefLoading(true)
    setPersonaLoading(true)
    setIsApiFailed(false)

    // Request id for the stream. Generated per run, not per render.
    const timestamp = Date.now().toString()

    const tasks = [
      (async () => {
        let controller: AbortController | null = null
        try {
          setIsStreaming(true)
          const partial: Record<string, any> = { summary: {} }
          let identityReady = false
          let streamErrored = false
          let receivedFrame = false
          controller = new AbortController()
          streamAbortRef.current = controller
          await getSinglePersonBriefStream(
            tokenInfo as ITokenInfo,
            linkedin_url,
            personName,
            account_name,
            accountId!,
            `${timestamp}-kyp`,
            (event: string, data: unknown) => {
              if (event === "error") {
                streamErrored = true
                setIsApiFailed(true)
                setPersonBriefLoading(false)
                return
              }
              if (event === "done") {
                setPersonBrief({ ...partial })
                setPersonBriefLoading(false)
                return
              }
              receivedFrame = true
              applyPersonBriefFrame(partial, event, data)
              setPersonBrief({ ...partial })
              // Render as soon as identity lands so the stream is visible
              // progressively rather than behind the loader.
              if (event === "meta" && !identityReady) {
                identityReady = true
                setPersonBriefLoading(false)
              }
              if (event === "summary") setPersonBriefLoading(false)
            },
            accountDbId,
            controller.signal
          )
          if (!streamErrored && !receivedFrame) {
            // Ended with no frames and no error frame — surface a failure
            // rather than leaving a blank body.
            setIsApiFailed(true)
          } else if (!streamErrored) {
            track(EVENTS.PERSON_BRIEF_CLICKED, {
              ...getBaseEventProps({
                feature: "pre_meeting_brief",
                accountId,
                accountName: account_name
              }),
              executive_name: personName,
              title: "",
              account_name,
              source
            })
          }
        } catch (err) {
          if ((err as { name?: string })?.name !== "AbortError") {
            setIsApiFailed(true)
          }
        } finally {
          // A newer run superseded this stream — don't clear its flags.
          if (streamAbortRef.current === controller) {
            setIsStreaming(false)
            setPersonBriefLoading(false)
          }
        }
      })(),
      (async () => {
        try {
          pollCancelledRef.current = false
          const response = await generatePersona(tokenInfo!, { linkedin_url })
          const state = response.data.data.persona_idetification_state
          if (state === "IDENTIFICATION_IN_PROGRESS") {
            await pollPersona()
          } else {
            setPersona(response.data.data)
            track(AMPLITUDE_EVENTS.PERSONA.FETCH_PERSONA, {
              ...getBaseEventProps({
                feature: "pre_meeting_brief",
                accountId,
                accountName: account_name
              }),
              name: response.data.data.name,
              id: response.data.data.id,
              state: state === "IDENTIFIED" ? "success" : "failed"
            })
          }
        } catch {
          // Secondary section: degrade silently, leaving persona unset so the
          // section is simply absent. Setting isApiFailed here would blank a
          // perfectly good brief.
          setPersona(null)
        } finally {
          setPersonaLoading(false)
        }
      })()
    ]

    await Promise.allSettled(tasks)
  }, [
    linkedin_url,
    personName,
    account_name,
    accountId,
    accountDbId,
    source,
    tokenInfo,
    pollPersona,
    onMissingLinkedin
  ])

  return {
    personBrief,
    persona,
    isApiFailed,
    isStreaming,
    isPersonBriefLoading,
    personaLoading,
    start,
    cancel,
    reset
  }
}
